import {
	Box,
	Checkbox,
	Divider,
	Flex,
	Group,
	Stack,
	createStyles,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { showNotification } from '@mantine/notifications';
import {
	useCreateAiTask,
	useGetPersonas,
	useGetSlackConversations,
	useUpdateAiTask,
	type AiTaskOut,
} from '@repo/api-codegen';
import { trackEvent } from '@repo/common/analytics';
import { Button, Icon, Select, Text, TextInput } from '@repo/foundations';
import { memo, useCallback, useMemo } from 'react';
import { queryClient, useAuthUser } from '../../api';
import { RichEditor } from '../../components/RichEditor';
import { NotificationMessages } from './constants';
import { ScheduleInput } from './ScheduleInput';
import type { AgentFormValues } from './types';

const useStyles = createStyles((theme) => ({
	editorWrapper: {
		border: `0.5px solid ${theme.other.getColor('border/input/default')}`,
		borderRadius: theme.radius.sm,
		backgroundColor: theme.other.getColor('surface/input/default'),
		padding: theme.spacing.xs,

		'.ProseMirror': {
			fontSize: theme.fontSizes.sm,
			minHeight: theme.other.space[32],
		},
	},

	editorWrapperError: {
		border: `0.5px solid ${theme.other.getColor('border/critical-secondary/default')}`,
		borderRadius: theme.radius.sm,
		backgroundColor: theme.other.getColor('surface/critical/default'),
		padding: theme.spacing.xs,

		'.ProseMirror': {
			fontSize: theme.fontSizes.sm,
			minHeight: theme.other.space[32],
		},
	},

	dangerZone: {
		borderRadius: theme.radius.sm,
		padding: theme.spacing.md,
		backgroundColor: theme.other.getColor('surface/critical/default'),
		border: `1px solid ${theme.other.getColor('border/critical/default')}`,
	},

	tabPanel: {
		paddingTop: theme.spacing.md,
	},
}));

interface AgentFormProps {
	form: ReturnType<typeof useForm<AgentFormValues>>;
	personas?: Array<{ id: string; name: string }>;
	isLoadingPersonas: boolean;
	editingAgent: Partial<AiTaskOut> | null;
	onSubmit: (values: AgentFormValues) => Promise<void>;
	onCancel: () => void;
	isSubmitting: boolean;
	isTemplate?: boolean;
}

const MAX_LENGTH = 50;

/**
 * Component for displaying and managing agent form fields
 */
const AgentForm = memo(
	({
		form,
		personas,
		isLoadingPersonas,
		isSubmitting,
		editingAgent,
		onSubmit,
		onCancel,
		isTemplate,
	}: AgentFormProps) => {
		const { classes } = useStyles();

		// Fetch Slack channels for notification options
		const { data: slackChannels = [], isLoading: isLoadingSlackChannels } =
			useGetSlackConversations({
				queryParams: { refresh: false },
			});

		return (
			<form onSubmit={form.onSubmit(onSubmit)}>
				<Stack spacing="md">
					<TextInput
						autoFocus
						label="Title"
						placeholder="Enter agent name"
						maxLength={MAX_LENGTH}
						rightSection={
							<Text size="xs" color="text/secondary/default">
								{form.values.title?.length || 0}/{MAX_LENGTH}
							</Text>
						}
						{...form.getInputProps('title')}
					/>
					<Select
						label="Persona"
						placeholder="Select a persona (optional)"
						data={[
							{ value: '', label: 'Default (Secoda AI)' },
							...(personas?.map((persona) => ({
								value: persona.id,
								label: persona.name || 'Unnamed persona',
							})) || []),
						]}
						value={form.values.persona_id || ''}
						onChange={(value) => form.setFieldValue('persona_id', value || '')}
						isLoading={isLoadingPersonas}
						clearable
						searchable
						rightSection={
							<Icon color="icon/secondary/default" name="selector" />
						}
						styles={{
							rightSection: {
								pointerEvents: 'none',
							},
						}}
					/>
					<Stack spacing="3xs">
						<Text size="sm" fw="semibold">
							Prompt
						</Text>
						<Box
							className={
								form.errors.prompt
									? classes.editorWrapperError
									: classes.editorWrapper
							}
						>
							<RichEditor
								readOnly={false}
								initialValue={form.values.prompt}
								onChangeCallback={(value?: string) => {
									form.setFieldValue('prompt', value || '');
								}}
								emptyLinePlaceholder="Enter the AI prompt for this agent..."
								limited={true}
								disableTopGap={true}
							/>
						</Box>
						<Text size="xs" color="text/secondary/default">
							Tip: Be specific about what what you want the agent to analyze and
							what actions to take based on findings. Use @ to mention specific
							tables, dashboards, or resources.
						</Text>
						{form.errors.prompt && (
							<Group spacing="xs">
								<Icon name="alertCircle" color="text/critical/default" />
								<Text size="sm" color="text/critical/default">
									{form.errors.prompt}
								</Text>
							</Group>
						)}
					</Stack>

					<ScheduleInput
						cronExpression={form.values.cron_expression || '0 9 * * *'}
						onChange={(cronExpression) => {
							form.setFieldValue('cron_expression', cronExpression);
						}}
					/>

					<Select
						label="Notify Slack channel (Optional)"
						placeholder="Select"
						data={[
							{ value: '', label: 'No notifications' },
							...slackChannels.map((channel) => ({
								value: channel.id,
								label: `#${channel.name}`,
							})),
						]}
						value={form.values.slack_channel_id || ''}
						onChange={(value) =>
							form.setFieldValue('slack_channel_id', value || '')
						}
						isLoading={isLoadingSlackChannels}
						clearable
						searchable
						rightSection={
							<Icon color="icon/secondary/default" name="selector" />
						}
						styles={{
							rightSection: {
								pointerEvents: 'none',
							},
						}}
					/>

					<Checkbox
						label="Allow the agent to search the web for additional context"
						labelPosition="right"
						{...form.getInputProps('web_search', { type: 'checkbox' })}
					/>

					<Divider />
					<Flex justify="space-between">
						<Group position="left" spacing="xs">
							<Text size="sm">
								Learn more about{' '}
								<a href="https://docs.secoda.com/features/ai-assistant/secoda-ai-agents">
									agents
								</a>
							</Text>
						</Group>
						<Group position="right" spacing="xs">
							<Button variant="tertiary" type="button" onClick={onCancel}>
								Cancel
							</Button>
							<Button type="submit" variant="primary" loading={isSubmitting}>
								{isTemplate || !editingAgent ? 'Create agent' : 'Save'}
							</Button>
						</Group>
					</Flex>
				</Stack>
			</form>
		);
	}
);

export function AgentEditor({
	agent,
	isTemplate,
	fromAgentMessage,
	onCancel,
}: {
	agent?: AiTaskOut | null;
	isTemplate?: boolean;
	fromAgentMessage?: boolean;
	onCancel: () => void;
}) {
	const { data: personas, isLoading: isLoadingPersonas } = useGetPersonas({});
	const { user, workspace } = useAuthUser();

	// Map personas to the expected format for the form
	const mappedPersonas = useMemo(
		() =>
			personas?.map((persona) => ({
				id: persona.id,
				name: persona.name || 'Unnamed persona',
			})) || [],
		[personas]
	);

	const form = useForm<AgentFormValues>({
		initialValues: {
			title: agent?.title || '',
			prompt: agent?.prompt || '',
			web_search: agent?.web_search || false,
			slack_channel_id: agent?.slack_channel_id || '',
			cron_expression: agent?.cron_expression || '0 9 * * *', // Default to daily at 9 AM
			persona_id: agent?.persona?.id || '',
		},
		validate: {
			title: (value) => (!value ? 'Title is required' : null),
			prompt: (value) => (!value ? 'Prompt is required' : null),
		},
	});

	const { mutateAsync: createAgent, isPending: isCreating } = useCreateAiTask({
		onSuccess: (data) => {
			// Invalidate all AI-related queries to ensure fresh data
			queryClient.invalidateQueries();
			trackEvent(
				'ai/agent/create',
				{
					task_id: data?.id ?? '',
					title: data?.title ?? '',
					persona_id: data?.persona?.id ?? '',
					is_template: isTemplate ? 'true' : 'false',
					from_agent_message: fromAgentMessage ? 'true' : 'false',
				},
				user,
				workspace
			);
			showNotification({
				...NotificationMessages.AGENT_CREATED,
				color: 'green',
				icon: <Icon name="check" color="text/success-on-fill/default" />,
			});
			onCancel();
		},
	});

	const { mutateAsync: updateAgent, isPending: isUpdating } = useUpdateAiTask({
		onSuccess: () => {
			// Invalidate all AI-related queries to ensure fresh data
			queryClient.invalidateQueries();
			showNotification({
				...NotificationMessages.AGENT_UPDATED,
				color: 'green',
				icon: <Icon name="check" color="text/success-on-fill/default" />,
			});
			onCancel();
		},
	});

	const handleSubmit = useCallback(
		async (values: AgentFormValues) => {
			try {
				if (agent?.id) {
					await updateAgent({
						pathParams: { taskId: agent.id },
						body: values,
					});
				} else {
					await createAgent({ body: values });
				}
			} catch {
				showNotification({
					...NotificationMessages.AGENT_SAVE_ERROR,
					color: 'red',
					icon: (
						<Icon name="alertCircle" color="text/critical-on-fill/default" />
					),
				});
			}
		},
		[agent, updateAgent, createAgent]
	);

	return (
		<AgentForm
			form={form}
			personas={mappedPersonas}
			isLoadingPersonas={isLoadingPersonas}
			editingAgent={agent ?? null}
			onSubmit={handleSubmit}
			onCancel={onCancel}
			isSubmitting={isCreating || isUpdating}
			isTemplate={isTemplate}
		/>
	);
}
